import { useBreakpoint } from "@simplybusiness/mobius";
import classNames from "classnames/dedupe";
import type React from "react";
import {
  useCurrentSection,
  useQuestionnaireSubmit,
} from "../../../features/questionnaire";
import type { ActionsVariant } from "../Actions";
import { QuestionnaireActions, Actions } from "../Actions";
import type { SectionProps } from "../Section";

export interface QuestionnaireFormProps {
  submitActionLabel: string;
  backAction?: { label?: string; url?: string };
  sections: SectionProps[];
  children?: React.ReactNode;
  questionnaireVariant?: "stacked-sections";
}

export const QuestionnaireForm = (props: QuestionnaireFormProps) => {
  const {
    submitActionLabel,
    backAction,
    sections,
    children,
    questionnaireVariant,
  } = props;

  // Form submission
  const { formRef, handleSubmit, inputValues, tokens, url } =
    useQuestionnaireSubmit();

  // UI state
  const { up } = useBreakpoint();
  const isDesktop = up("md");
  const { currentSection, isFirstSection } = useCurrentSection(sections);

  // Computed values
  const isEditSection = currentSection?.displayAs === "edit";
  const shouldSkipActions =
    isEditSection || currentSection?.displayAs === "review";
  const isCoverGenius = ["cover_genius", "personalised_cover"].includes(
    currentSection?.displayAs || "",
  );

  const submitOnly = questionnaireVariant === "stacked-sections";

  let actionsVariant: ActionsVariant | undefined;
  if (submitOnly) {
    actionsVariant = "stacked";
  } else if (isCoverGenius) {
    actionsVariant = currentSection?.displayAs as
      | "cover_genius"
      | "personalised_cover";
  }

  return (
    <form
      ref={formRef}
      className="mobius-submission__questionnaire-form"
      action={url}
      method="POST"
      acceptCharset="UTF-8"
      noValidate
      onSubmit={handleSubmit}
      aria-label="Questionnaire form"
    >
      {inputValues.map(([key, value], i) => (
        <input key={`${key}-${i}`} type="hidden" name={key} value={value} />
      ))}
      <input
        type="hidden"
        name="authenticity_token"
        value={tokens.authenticityToken}
      />
      <input type="hidden" name="journey_token" value={tokens.journeyToken} />
      {isDesktop && !isEditSection && !isFirstSection && !submitOnly && (
        <QuestionnaireActions
          data-testid="desktop-actions"
          className={classNames("mobius-questionnaire__actions-container", {
            "--is-first-section": isFirstSection,
          })}
        >
          <QuestionnaireActions.Back />
        </QuestionnaireActions>
      )}
      {children}
      {!shouldSkipActions && (
        <Actions
          variant={actionsVariant}
          submitActionLabel={submitActionLabel}
          backAction={backAction}
        />
      )}
    </form>
  );
};
